"use client";

import { useState, useEffect } from "react";
import { Project } from "@prisma/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

interface RenameProjectModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  project: Project;
  onSubmit: (newName: string) => void;
  isPending: boolean;
}

export function RenameProjectModal({
  isOpen,
  onOpenChange,
  project,
  onSubmit,
  isPending,
}: RenameProjectModalProps) {
  const [newName, setNewName] = useState(project.name);

  // Update state if the project prop changes while modal is open
  useEffect(() => {
    if (project) {
      setNewName(project.name);
    }
  }, [project]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newName.trim() && newName.trim() !== project.name) {
      onSubmit(newName.trim());
    } else {
      // Optionally handle case where name is unchanged or empty
      onOpenChange(false); // Close if name is same or empty
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Rename Project</DialogTitle>
            <DialogDescription>
              Enter a new name for the project "{project.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                New Name
              </Label>
              <Input
                id="name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isPending || !newName.trim() || newName.trim() === project.name
              }
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Rename Project
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
